import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Check, Star, Shield, Clock, ArrowRight, ArrowLeft, ShoppingCart, Globe, Server, Palette, Search, FileText, MapPin, ChevronDown, ChevronUp, Lock, Zap, RefreshCw, Phone } from "lucide-react";
import { gtmPurchaseStep, gtmBeginCheckout, gtmPurchaseComplete, gtmClick, gtmPhoneClick } from "@/lib/gtm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hardcoded IDs must match backend PACKAGES_DEFINED keys
const PACKAGE_ID_MAP = { "Starter": "starter", "Business": "business", "eCommerce": "ecommerce" };

const ADDONS = [
  { id: "domain", icon: Globe, name: "Domain Name (.com.au)", desc: "Secure your .com.au domain for 1 year", price: 10, per: "/yr", popular: false },
  { id: "hosting", icon: Server, name: "Website Hosting", desc: "Australian hosting, SSL, backups & support — billed annually", price: 99, per: "/yr", popular: true },
  { id: "logo", icon: Palette, name: "Logo Design Package", desc: "Custom logo, brand colours & style guide", price: 149, per: "", popular: false },
  { id: "seo", icon: Search, name: "SEO Setup & Analysis", desc: "Full SEO setup and analysis to get you found on Google from day one", price: 299, per: "", popular: false },
  { id: "copywriting", icon: FileText, name: "Professional Copywriting", desc: "Expertly written copy for all your pages", price: 199, per: "", popular: false },
  { id: "gmb", icon: MapPin, name: "Google My Business Setup", desc: "Appear on Google Maps and local search", price: 99, per: "", popular: false },
];

const REVIEWS = [
  { name: "Sarah Mitchell", business: "Mitchell's Cafe, Richmond", text: "Couldn't believe the quality for $499. Live in 36 hours and I've had 3 new bookings through it already. Worth every cent.", rating: 5, pkg: "Starter" },
  { name: "Dave Kowalski", business: "Kowalski Plumbing, Dandenong", text: "This paid for itself in the first week. Calls are up, Google ranking is up. Should've done it years ago!", rating: 5, pkg: "Business" },
  { name: "Priya Sharma", business: "Priya's Yoga Studio, St Kilda", text: "The booking system alone saves me hours every week. Professional, fast, and the team actually cared about my business.", rating: 5, pkg: "Business" },
];

const STEPS = ["Choose Package", "Add Extras", "How It Works", "Your Details", "Review & Pay"];

function StepIndicator({ current }) {
  return (
    <div className="flex items-center justify-center mb-10" data-testid="step-indicator">
      {STEPS.map((s, i) => (
        <div key={i} className="flex items-center">
          <div className={`flex flex-col items-center`}>
            <div className={`w-10 h-10 rounded-full border-2 border-black flex items-center justify-center font-black text-sm transition-all ${
              i + 1 < current ? "bg-green-500 text-white" : i + 1 === current ? "bg-[#FFD600]" : "bg-white text-gray-400"
            }`} style={i + 1 <= current ? { boxShadow: "2px 2px 0px 0px rgba(0,0,0,1)" } : {}}>
              {i + 1 < current ? <Check size={14} strokeWidth={3} /> : i + 1}
            </div>
            <span className={`text-xs font-bold mt-1 hidden sm:block ${i + 1 === current ? "text-black" : "text-gray-400"}`}>{s}</span>
          </div>
          {i < STEPS.length - 1 && (
            <div className={`h-0.5 w-8 sm:w-16 mx-1 sm:mx-2 transition-colors ${i + 1 < current ? "bg-green-500" : "bg-gray-200"}`} />
          )}
        </div>
      ))}
    </div>
  );
}

function OrderSummary({ selectedPackage, selectedAddons, paymentMode, compact = false }) {
  const total = (selectedPackage?.price || 0) + selectedAddons.reduce((s, a) => s + a.price, 0);
  const monthlyAmount = Math.ceil((total * 1.25 / 12) * 100) / 100;
  if (!selectedPackage) return null;
  return (
    <div className={`bg-[#FFD600] border-2 border-black rounded-2xl p-6 ${compact ? "" : "sticky top-24"}`} style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }} data-testid="order-summary">
      <h3 className="font-black text-lg mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Order Summary</h3>
      <div className="space-y-2 mb-4">
        <div className="flex justify-between font-bold text-sm">
          <span>{selectedPackage.name} Website</span>
          <span>${selectedPackage.price}</span>
        </div>
        {selectedAddons.map(a => (
          <div key={a.id} className="flex justify-between text-sm">
            <span className="truncate pr-2">{a.name}</span>
            <span className="font-semibold shrink-0">${a.price}{a.per}</span>
          </div>
        ))}
      </div>
      <div className="border-t-2 border-black pt-3">
        {paymentMode === "monthly" ? (
          <div>
            <div className="flex justify-between font-black text-xl" style={{ fontFamily: "Outfit, sans-serif" }}>
              <span>Per Month</span>
              <span>${monthlyAmount.toFixed(2)}/mo</span>
            </div>
            <p className="text-xs font-semibold mt-1 opacity-70">12 monthly payments</p>
          </div>
        ) : (
          <div className="flex justify-between font-black text-xl" style={{ fontFamily: "Outfit, sans-serif" }}>
            <span>Total</span>
            <span>${total.toLocaleString()}</span>
          </div>
        )}
      </div>
      <div className="mt-4 flex items-center gap-2 text-xs font-bold">
        <Shield size={12} /> 30-day money back guarantee
      </div>
      <div className="flex items-center gap-2 text-xs font-bold mt-1">
        <Clock size={12} /> Live within 48 hours
      </div>
    </div>
  );
}

export default function PurchasePage() {
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [paymentMode, setPaymentMode] = useState("upfront"); // "upfront" or "monthly"
  const [form, setForm] = useState({ name: "", email: "", phone: "", business_name: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [pollStatus, setPollStatus] = useState("idle"); // idle, polling, success, failed
  const [purchaseData, setPurchaseData] = useState(null);

  // Fetch packages from API
  useEffect(() => {
    axios.get(`${API}/packages`).then(r => {
      const pkgs = r.data.map(p => ({
        ...p,
        id: PACKAGE_ID_MAP[p.name] || p.name.toLowerCase(),
        popular: p.is_popular,
      }));
      setPackages(pkgs);
    }).catch(() => {});
  }, []);

  // Pre-select package from URL param
  useEffect(() => {
    if (packages.length === 0) return;
    const params = new URLSearchParams(location.search);
    const pkg = params.get("package");
    if (pkg) {
      const found = packages.find(p => p.id === pkg);
      if (found) { setSelectedPackage(found); setStep(2); }
    }
  }, [location.search, packages]);

  const pollPayment = async (sessionId, attempts = 0) => {
    if (attempts > 10) { setPollStatus("failed"); return; }
    try {
      const res = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        setPurchaseData(res.data);
        gtmPurchaseComplete(selectedPackage?.name || "unknown", total, res.data.ticket_number, form.email);
        setPollStatus("success");
      } else if (res.data.status === "expired") {
        setPollStatus("failed");
      } else {
        setTimeout(() => pollPayment(sessionId, attempts + 1), 2000);
      }
    } catch {
      setTimeout(() => pollPayment(sessionId, attempts + 1), 2000);
    }
  };

  const toggleAddon = (addon) => {
    setSelectedAddons(prev =>
      prev.find(a => a.id === addon.id) ? prev.filter(a => a.id !== addon.id) : [...prev, addon]
    );
  };

  const handleCheckout = async () => {
    if (!form.name || !form.email) { toast.error("Please fill in your name and email"); return; }
    setLoading(true);
    try {
      gtmBeginCheckout(selectedPackage.name, total, selectedAddons.map(a => a.name));
      const res = await axios.post(`${API}/checkout/create`, {
        package_id: selectedPackage.id,
        addons: selectedAddons.map(a => a.id),
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: form.phone,
        business_name: form.business_name,
        notes: form.notes,
        origin_url: window.location.origin,
        payment_type: paymentMode,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error("Payment setup failed. Please try again or call 0466 476 264.");
      setLoading(false);
    }
  };

  const total = (selectedPackage?.price || 0) + selectedAddons.reduce((s, a) => s + a.price, 0);
  const monthlyAmount = Math.ceil((total * 1.25 / 12) * 100) / 100;

  // Success page (only show when polling or payment complete/failed)
  if (pollStatus !== "idle") {
    return (
      <main className="pt-16 min-h-screen bg-[#F4F4F5]" data-testid="purchase-success-page">
        <div className="container-main max-w-2xl py-20 text-center">
          {pollStatus === "polling" && (
            <div>
              <RefreshCw size={48} className="mx-auto mb-6 animate-spin text-[#0055FF]" />
              <h1 className="text-4xl font-black mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Confirming Payment...</h1>
              <p className="text-lg text-gray-600">Please wait while we confirm your payment.</p>
            </div>
          )}
          {pollStatus === "success" && (
            <div className="animate-fade-in-up">
              <div className="w-24 h-24 bg-green-500 border-4 border-black rounded-full flex items-center justify-center mx-auto mb-6" style={{ boxShadow: "6px 6px 0px 0px rgba(0,0,0,1)" }}>
                <Check size={48} className="text-white" strokeWidth={3} />
              </div>
              <h1 className="text-5xl font-black mb-4" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="success-title">Your Website Order is In!</h1>
              <div className="bg-[#FFD600] border-2 border-black rounded-2xl p-6 mb-8 inline-block" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
                <p className="text-sm font-black uppercase tracking-wider mb-1">Your Order Ticket</p>
                <p className="text-4xl font-black" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="ticket-number">{purchaseData?.ticket_number}</p>
              </div>
              <div className="bg-white border-2 border-black rounded-2xl p-8 mb-8 text-left" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
                <h3 className="font-black text-xl mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>What Happens Next?</h3>
                {[
                  "Check your email — a confirmation has been sent",
                  "Our team will contact you within 24 hours",
                  "We'll need your logo, photos, and content",
                  "Your website goes live within 48 hours of us receiving everything",
                ].map((s, i) => (
                  <div key={i} className="flex items-start gap-3 mb-3">
                    <div className="w-7 h-7 bg-[#FFD600] border-2 border-black rounded-full flex items-center justify-center shrink-0 font-black text-xs">{i + 1}</div>
                    <p className="font-semibold text-sm">{s}</p>
                  </div>
                ))}
              </div>
              <p className="font-bold text-lg mb-2">Questions? Call us: <a href="tel:0466476264" className="text-[#0055FF] underline">0466 476 264</a></p>
              <p className="text-gray-600 mb-8">Or email: hello@nofusswebsites.com.au</p>
              <Link to="/" className="btn-primary inline-flex items-center gap-2">Back to Home</Link>
            </div>
          )}
          {pollStatus === "failed" && (
            <div>
              <h1 className="text-4xl font-black mb-4">Payment Not Completed</h1>
              <p className="text-lg text-gray-600 mb-8">Your payment was cancelled or expired. No charges were made.</p>
              <button onClick={() => { setStep(4); setPollStatus("idle"); window.history.replaceState({}, "", "/purchase"); }} className="btn-primary">Try Again</button>
            </div>
          )}
        </div>
      </main>
    );
  }

  return (
    <main className="pt-16 bg-[#F4F4F5] min-h-screen" data-testid="purchase-page">
      {/* Header */}
      <div className="bg-[#FFD600] border-b-4 border-black py-10 text-center">
        <h1 className="text-4xl md:text-5xl font-black" style={{ fontFamily: "Outfit, sans-serif" }}>Get Your Website</h1>
        <p className="font-semibold mt-2">Professional. Fast. Guaranteed live in 48 hours.</p>
      </div>

      <div className="container-main max-w-6xl py-12">
        <StepIndicator current={step} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">

            {/* Step 1: Choose Package */}
            {step === 1 && (
              <div data-testid="step-1">
                <h2 className="text-3xl font-black mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Choose Your Package</h2>

                {/* Payment Mode Toggle */}
                <div className="flex items-center justify-center gap-1 bg-black rounded-full p-1 mb-6 max-w-sm mx-auto" data-testid="payment-mode-toggle">
                  <button
                    onClick={() => setPaymentMode("upfront")}
                    className={`flex-1 py-2.5 px-4 rounded-full text-sm font-black transition-all ${paymentMode === "upfront" ? "bg-[#FFD600] text-black" : "text-gray-400 hover:text-white"}`}
                    data-testid="toggle-upfront"
                  >
                    Pay Upfront
                  </button>
                  <button
                    onClick={() => setPaymentMode("monthly")}
                    className={`flex-1 py-2.5 px-4 rounded-full text-sm font-black transition-all ${paymentMode === "monthly" ? "bg-[#FFD600] text-black" : "text-gray-400 hover:text-white"}`}
                    data-testid="toggle-monthly"
                  >
                    Pay Monthly
                  </button>
                </div>

                <div className="space-y-4">
                  {packages.map(pkg => {
                    const monthlyPrice = Math.ceil((pkg.price * 1.25 / 12) * 100) / 100;
                    return (
                    <div
                      key={pkg.id}
                      onClick={() => { setSelectedPackage(pkg); gtmPurchaseStep(1, "select_package", { package: pkg.name, price: pkg.price }); }}
                      className={`border-2 border-black rounded-2xl p-6 cursor-pointer transition-all hover:-translate-y-0.5 ${
                        selectedPackage?.id === pkg.id ? "bg-[#FFD600]" : "bg-white"
                      }`}
                      style={{ boxShadow: selectedPackage?.id === pkg.id ? "4px 4px 0px 0px rgba(0,0,0,1)" : "2px 2px 0px 0px rgba(0,0,0,0.4)" }}
                      data-testid={`select-package-${pkg.id}`}
                    >
                      <div className="flex items-start justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-4">
                          <div className={`w-6 h-6 rounded-full border-2 border-black flex items-center justify-center shrink-0 ${selectedPackage?.id === pkg.id ? "bg-black" : "bg-white"}`}>
                            {selectedPackage?.id === pkg.id && <div className="w-3 h-3 bg-[#FFD600] rounded-full" />}
                          </div>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="text-xl font-black" style={{ fontFamily: "Outfit, sans-serif" }}>{pkg.name}</h3>
                              {pkg.popular && <span className="bg-black text-[#FFD600] text-xs font-black px-2 py-0.5 rounded-full">Most Popular</span>}
                            </div>
                            <p className="text-sm text-gray-600">{pkg.tagline}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          {paymentMode === "monthly" ? (
                            <>
                              <p className="text-3xl font-black" style={{ fontFamily: "Outfit, sans-serif" }}>${monthlyPrice.toFixed(2)}<span className="text-base font-bold">/mo</span></p>
                              <p className="text-xs text-gray-500">for 12 months</p>
                            </>
                          ) : (
                            <>
                              {pkg.original_price && pkg.original_price > pkg.price && (
                                <p className="text-lg line-through text-gray-400">${pkg.original_price}</p>
                              )}
                              <p className="text-3xl font-black" style={{ fontFamily: "Outfit, sans-serif" }}>${pkg.price}</p>
                              <p className="text-xs text-gray-500">one-time</p>
                              {pkg.original_price && pkg.original_price > pkg.price && (
                                <span className="bg-red-500 text-white text-xs font-black px-2 py-0.5 rounded-full mt-1 inline-block">
                                  {Math.round(((pkg.original_price - pkg.price) / pkg.original_price) * 100)}% OFF
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      {selectedPackage?.id === pkg.id && (
                        <div className="mt-4 pt-4 border-t-2 border-black grid grid-cols-2 gap-1.5">
                          {(pkg.features || []).map((f, i) => (
                            <div key={i} className="flex items-center gap-1.5 text-xs font-semibold">
                              <Check size={12} className="text-green-700 shrink-0" strokeWidth={3} />
                              {f}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
                <div className="mt-8 flex justify-end">
                  <button
                    onClick={() => { if (!selectedPackage) { toast.error("Please select a package"); return; } gtmPurchaseStep(2, "add_extras", { package: selectedPackage.name }); setStep(2); }}
                    className="btn-primary flex items-center gap-2 text-lg"
                    data-testid="next-step-1"
                  >
                    Next: Choose Extras <ArrowRight size={18} />
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Add-ons */}
            {step === 2 && (
              <div data-testid="step-2">
                <h2 className="text-3xl font-black mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Power Up Your Website</h2>
                <p className="text-gray-600 mb-6">Add optional extras to get even more from your website.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {ADDONS.map(addon => {
                    const active = selectedAddons.find(a => a.id === addon.id);
                    return (
                      <div
                        key={addon.id}
                        onClick={() => toggleAddon(addon)}
                        className={`border-2 border-black rounded-2xl p-5 cursor-pointer transition-all hover:-translate-y-0.5 relative ${active ? "bg-[#FFD600]" : "bg-white"}`}
                        style={{ boxShadow: active ? "4px 4px 0px 0px rgba(0,0,0,1)" : "2px 2px 0px 0px rgba(0,0,0,0.4)" }}
                        data-testid={`addon-${addon.id}`}
                      >
                        {addon.popular && (
                          <span className="absolute -top-2 -right-2 bg-black text-[#FFD600] text-xs font-black px-2 py-0.5 rounded-full border border-black">Popular</span>
                        )}
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 border-2 border-black rounded-xl flex items-center justify-center shrink-0 ${active ? "bg-black text-[#FFD600]" : "bg-[#F4F4F5]"}`}>
                              <addon.icon size={16} />
                            </div>
                            <div>
                              <p className="font-black text-sm">{addon.name}</p>
                              <p className="text-xs text-gray-600 mt-0.5">{addon.desc}</p>
                            </div>
                          </div>
                          <div className="text-right shrink-0 ml-2">
                            <p className="font-black">${addon.price}</p>
                            {addon.per && <p className="text-xs text-gray-500">{addon.per}</p>}
                          </div>
                        </div>
                        {active && (
                          <div className="absolute top-3 left-3">
                            <div className="w-5 h-5 bg-green-500 border-2 border-black rounded-full flex items-center justify-center">
                              <Check size={10} className="text-white" strokeWidth={3} />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-8 flex justify-between">
                  <button onClick={() => setStep(1)} className="btn-secondary flex items-center gap-2"><ArrowLeft size={16} /> Back</button>
                  <button onClick={() => setStep(3)} className="btn-primary flex items-center gap-2 text-lg" data-testid="next-step-2"
                    onClickCapture={() => gtmPurchaseStep(3, "how_it_works", { addons: selectedAddons.map(a => a.name).join(", ") })}>
                    Next: How It Works <ArrowRight size={18} />
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: How It Works - Trust Builder */}
            {step === 3 && (
              <div data-testid="step-3-process">
                <h2 className="text-3xl font-black mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Here's How It Works</h2>
                <p className="text-gray-600 mb-8">A simple, transparent process from start to finish.</p>

                <div className="relative">
                  {[
                    { icon: ShoppingCart, title: "Choose Your Package", desc: "Pick the plan that fits your business.", color: "bg-[#FFD600]" },
                    { icon: Lock, title: "Pay Securely", desc: "256-bit SSL encryption via Stripe. Card details never stored.", badge: "Secure" },
                    { icon: Shield, title: "Money Back Guarantee", desc: "Full refund within 30 days, no questions asked.", badge: "Risk Free" },
                    { icon: FileText, title: "We Send You a Content Form", desc: "A simple form to collect your logo, photos, text & preferences." },
                    { icon: Zap, title: "Delivered in 48 Hours", desc: "Your professional website goes live. That's our promise.", badge: "Fast" },
                    { icon: RefreshCw, title: "Revisions Included", desc: "Not 100% happy? We'll tweak it until you are." },
                  ].map((s, i, arr) => (
                    <div key={i} className="flex items-start gap-4 mb-2">
                      {/* Icon column with connecting line */}
                      <div className="flex flex-col items-center shrink-0">
                        <div className={`w-11 h-11 ${s.color || "bg-white"} border-2 border-black rounded-xl flex items-center justify-center`} style={{ boxShadow: "2px 2px 0px 0px rgba(0,0,0,1)" }}>
                          <s.icon size={18} />
                        </div>
                        {i < arr.length - 1 && <div className="w-0.5 h-6 bg-black mt-1" />}
                      </div>
                      {/* Content */}
                      <div className="pt-2 pb-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-black text-sm">{s.title}</h3>
                          {s.badge && <span className="text-[10px] font-black bg-green-100 text-green-700 border border-green-300 px-1.5 py-0.5 rounded-full">{s.badge}</span>}
                        </div>
                        <p className="text-sm text-gray-500 mt-0.5">{s.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                  <div className="bg-black text-white border-2 border-black rounded-2xl p-5" style={{ boxShadow: "3px 3px 0px 0px rgba(0,0,0,0.3)" }}>
                    <p className="font-black text-sm mb-1">All Done For You</p>
                    <p className="text-xs text-gray-300">Design, development, hosting, domain, SEO & launch — we handle everything.</p>
                  </div>
                  <div className="bg-black text-white border-2 border-black rounded-2xl p-5" style={{ boxShadow: "3px 3px 0px 0px rgba(0,0,0,0.3)" }}>
                    <p className="font-black text-sm mb-1">You Own Your Files</p>
                    <p className="text-xs text-gray-300">No lock-in. Want to self-host? We'll send everything through — it's yours.</p>
                  </div>
                </div>

                <div className="mt-8 flex justify-between">
                  <button onClick={() => setStep(2)} className="btn-secondary flex items-center gap-2"><ArrowLeft size={16} /> Back</button>
                  <button onClick={() => { gtmPurchaseStep(4, "your_details"); setStep(4); }} className="btn-primary flex items-center gap-2 text-lg" data-testid="next-step-3">
                    Next: Your Details <ArrowRight size={18} />
                  </button>
                </div>

                <div className="bg-[#FFD600] border-2 border-black rounded-2xl p-5 mt-6 text-center" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
                  <a href="tel:0466476264" className="inline-flex items-center gap-2 font-bold text-sm">
                    <Phone size={16} /> Have questions? Call or message 0466 476 264
                  </a>
                </div>
              </div>
            )}

            {/* Step 4: Details */}
            {step === 4 && (
              <div data-testid="step-4">
                <h2 className="text-3xl font-black mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Your Details</h2>
                <p className="text-gray-600 mb-6">Tell us a bit about yourself and your business.</p>
                <div className="bg-white border-2 border-black rounded-2xl p-8" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="text-xs font-black uppercase tracking-wider mb-1.5 block">Full Name *</label>
                      <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Jane Smith" className="w-full border-2 border-black rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD600]" data-testid="purchase-name" />
                    </div>
                    <div>
                      <label className="text-xs font-black uppercase tracking-wider mb-1.5 block">Email Address *</label>
                      <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} type="email" placeholder="jane@business.com.au" className="w-full border-2 border-black rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD600]" data-testid="purchase-email" />
                    </div>
                    <div>
                      <label className="text-xs font-black uppercase tracking-wider mb-1.5 block">Phone Number</label>
                      <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="0400 000 000" className="w-full border-2 border-black rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD600]" data-testid="purchase-phone" />
                    </div>
                    <div>
                      <label className="text-xs font-black uppercase tracking-wider mb-1.5 block">Business Name</label>
                      <input value={form.business_name} onChange={e => setForm({ ...form, business_name: e.target.value })} placeholder="Jane's Cafe" className="w-full border-2 border-black rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD600]" data-testid="purchase-business" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-black uppercase tracking-wider mb-1.5 block">Anything else we should know?</label>
                    <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} placeholder="Tell us about your business, what you do, or any special requirements..." className="w-full border-2 border-black rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD600] resize-none" data-testid="purchase-notes" />
                  </div>
                </div>
                <div className="mt-8 flex justify-between">
                  <button onClick={() => setStep(3)} className="btn-secondary flex items-center gap-2"><ArrowLeft size={16} /> Back</button>
                  <button onClick={() => { if (!form.name || !form.email) { toast.error("Name and email are required"); return; } gtmPurchaseStep(5, "review_and_pay", { name: form.name }); setStep(5); }}
                    className="btn-primary flex items-center gap-2 text-lg" data-testid="next-step-4">
                    Review & Pay <ArrowRight size={18} />
                  </button>
                </div>
              </div>
            )}

            {/* Step 5: Review & Pay */}
            {step === 5 && (
              <div data-testid="step-5">
                <h2 className="text-3xl font-black mb-6" style={{ fontFamily: "Outfit, sans-serif" }}>Review Your Order</h2>
                <div className="bg-white border-2 border-black rounded-2xl p-8 mb-6" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
                  <div className="grid grid-cols-2 gap-3 text-sm mb-6 pb-6 border-b-2 border-black">
                    {[["Name", form.name], ["Email", form.email], ["Phone", form.phone || "—"], ["Business", form.business_name || "—"]].map(([l, v]) => (
                      <div key={l}><p className="text-xs font-black uppercase tracking-wider text-gray-500 mb-0.5">{l}</p><p className="font-semibold">{v}</p></div>
                    ))}
                  </div>
                  <div className="space-y-2 text-sm mb-4">
                    <div className="flex justify-between font-bold">
                      <span>{selectedPackage?.name} Website</span>
                      <span>${selectedPackage?.price}</span>
                    </div>
                    {selectedAddons.map(a => (
                      <div key={a.id} className="flex justify-between">
                        <span>{a.name}</span>
                        <span className="font-semibold">${a.price}{a.per}</span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t-2 border-black pt-4">
                    {paymentMode === "monthly" ? (
                      <div>
                        <div className="flex justify-between font-black text-2xl" style={{ fontFamily: "Outfit, sans-serif" }}>
                          <span>Monthly Payment</span>
                          <span>${monthlyAmount.toFixed(2)}/mo</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1 text-right">12 monthly payments</p>
                      </div>
                    ) : (
                      <div className="flex justify-between font-black text-2xl" style={{ fontFamily: "Outfit, sans-serif" }}>
                        <span>Total Today</span>
                        <span>${total.toLocaleString()} AUD</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Payment Mode Toggle in Review */}
                <div className="flex items-center justify-center gap-1 bg-black rounded-full p-1 mb-6 max-w-sm mx-auto" data-testid="review-payment-toggle">
                  <button
                    onClick={() => setPaymentMode("upfront")}
                    className={`flex-1 py-2 px-4 rounded-full text-sm font-black transition-all ${paymentMode === "upfront" ? "bg-[#FFD600] text-black" : "text-gray-400 hover:text-white"}`}
                  >
                    Pay Upfront — ${total.toLocaleString()}
                  </button>
                  <button
                    onClick={() => setPaymentMode("monthly")}
                    className={`flex-1 py-2 px-4 rounded-full text-sm font-black transition-all ${paymentMode === "monthly" ? "bg-[#FFD600] text-black" : "text-gray-400 hover:text-white"}`}
                  >
                    Monthly — ${monthlyAmount.toFixed(2)}/mo
                  </button>
                </div>

                {/* Trust signals */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {[
                    { icon: Lock, text: "Secure SSL Payment" },
                    { icon: Shield, text: "30-Day Money Back" },
                    { icon: Clock, text: "Live in 48 Hours" },
                  ].map(({ icon: Icon, text }, i) => (
                    <div key={i} className="bg-[#F4F4F5] border-2 border-black rounded-xl p-3 text-center">
                      <Icon size={20} className="mx-auto mb-1" />
                      <p className="text-xs font-bold">{text}</p>
                    </div>
                  ))}
                </div>

                <button
                  onClick={handleCheckout}
                  disabled={loading}
                  className="w-full bg-[#FFD600] border-2 border-black font-black text-xl py-5 rounded-2xl flex items-center justify-center gap-3 transition-all hover:-translate-y-1 disabled:opacity-60"
                  style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}
                  data-testid="complete-purchase-btn"
                >
                  {loading ? <RefreshCw size={20} className="animate-spin" /> : <Lock size={20} />}
                  {loading ? "Redirecting to Payment..." : paymentMode === "monthly" ? `Pay $${monthlyAmount.toFixed(2)}/mo Securely →` : `Pay $${total.toLocaleString()} Securely →`}
                </button>
                <p className="text-xs text-center text-gray-500 mt-3 flex items-center justify-center gap-1">
                  <Lock size={10} /> Powered by Stripe · 256-bit SSL · Your card is never stored
                </p>
                <div className="mt-4 flex justify-start">
                  <button onClick={() => setStep(4)} className="text-sm font-bold flex items-center gap-1 hover:text-[#0055FF]"><ArrowLeft size={14} /> Back</button>
                </div>

                {/* Questions CTA */}
                <div className="mt-6 text-center">
                  <a href="tel:0466476264" className="inline-flex items-center gap-2 text-sm font-bold text-gray-600 hover:text-black transition-colors" data-testid="order-questions-cta">
                    <Phone size={14} /> Have questions about your order? Message or Call 0466 476 264
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <OrderSummary selectedPackage={selectedPackage} selectedAddons={selectedAddons} paymentMode={paymentMode} />

            {/* Guarantee */}
            <div className="bg-black text-white border-2 border-black rounded-2xl p-6" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#FFD600] border-2 border-white rounded-full flex items-center justify-center">
                  <Shield size={18} className="text-black" />
                </div>
                <h3 className="font-black" style={{ fontFamily: "Outfit, sans-serif" }}>30-Day Guarantee</h3>
              </div>
              <p className="text-sm text-gray-300 leading-relaxed">
                If you're not completely happy with your website within 30 days, we'll work with you until you are — or give you a full refund.
              </p>
            </div>
          </div>
        </div>

        {/* Reviews */}
        <div className="mt-16">
          <h2 className="text-3xl font-black mb-8 text-center" style={{ fontFamily: "Outfit, sans-serif" }}>What Our Customers Say</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {REVIEWS.map((r, i) => (
              <div key={i} className="bg-white border-2 border-black rounded-2xl p-6 flex flex-col" style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }} data-testid={`review-${i}`}>
                <div className="flex mb-4">
                  {[...Array(r.rating)].map((_, j) => <Star key={j} size={16} className="text-[#FFD600] fill-[#FFD600]" />)}
                </div>
                <p className="text-sm text-gray-700 leading-relaxed flex-1 italic mb-5">"{r.text}"</p>
                <div className="flex items-center gap-3 pt-4 border-t-2 border-black">
                  <div className="w-10 h-10 bg-[#FFD600] border-2 border-black rounded-full flex items-center justify-center font-black text-sm">{r.name[0]}</div>
                  <div>
                    <p className="font-black text-sm">{r.name}</p>
                    <p className="text-xs text-gray-500">{r.business}</p>
                  </div>
                  <span className="ml-auto text-xs font-bold bg-[#F4F4F5] border border-black px-2 py-0.5 rounded-full">{r.pkg}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}
